/**
 * Country and Year Selection Component
 * First step before loading the questionnaire - user selects country and tax year
 */

import React, { useState } from 'react';
import { Globe, Calendar, ArrowRight } from 'lucide-react';

export type Country = 'USA' | 'ES';
export type TaxYear = number;

interface CountryYearSelectionProps {
  onSelect: (country: Country, year: TaxYear) => void;
  initialCountry?: Country;
  initialYear?: TaxYear;
}

const COUNTRIES = [
  { code: 'USA' as Country, name: 'United States', flag: '🇺🇸' },
  { code: 'ES' as Country, name: 'Spain', flag: '🇪🇸' }
];

const CURRENT_YEAR = new Date().getFullYear();
const TAX_YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);

const CountryYearSelection: React.FC<CountryYearSelectionProps> = ({
  onSelect,
  initialCountry,
  initialYear = CURRENT_YEAR
}) => {
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(initialCountry || null);
  const [selectedYear, setSelectedYear] = useState<TaxYear>(initialYear);
  const [errors, setErrors] = useState<{ country?: string; year?: string }>({});

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    setErrors(prev => ({ ...prev, country: undefined }));
  };

  const handleYearSelect = (year: TaxYear) => {
    setSelectedYear(year);
    setErrors(prev => ({ ...prev, year: undefined }));
  };

  const handleContinue = () => {
    const newErrors: { country?: string; year?: string } = {};

    if (!selectedCountry) {
      newErrors.country = 'Please select a country';
    }

    if (!selectedYear || selectedYear < 2019 || selectedYear > CURRENT_YEAR + 1) {
      newErrors.year = 'Please select a valid tax year';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (selectedCountry) {
      onSelect(selectedCountry, selectedYear);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Tax Questionnaire Setup
          </h1>
          <p className="text-gray-600">
            Please select your country and tax year to begin the questionnaire
          </p>
        </div>

        {/* Country Selection */}
        <div className="mb-8">
          <label className="block text-sm font-semibold text-gray-700 mb-4 flex items-center">
            <Globe className="w-5 h-5 mr-2 text-primary-600" />
            Select Country
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {COUNTRIES.map((country) => (
              <button
                key={country.code}
                onClick={() => handleCountrySelect(country.code)}
                className={`
                  p-6 rounded-lg border-2 transition-all duration-200
                  ${
                    selectedCountry === country.code
                      ? 'border-primary-600 bg-primary-50 shadow-md'
                      : 'border-gray-200 bg-white hover:border-primary-300 hover:shadow-sm'
                  }
                `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-3xl">{country.flag}</span>
                    <div className="text-left">
                      <div className="font-semibold text-gray-900">{country.name}</div>
                      <div className="text-sm text-gray-500">Code: {country.code}</div>
                    </div>
                  </div>
                  {selectedCountry === country.code && (
                    <div className="w-6 h-6 rounded-full bg-primary-600 flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-white"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
          {errors.country && (
            <p className="mt-2 text-sm text-red-600">{errors.country}</p>
          )}
        </div>

        {/* Year Selection */}
        <div className="mb-8">
          <label className="block text-sm font-semibold text-gray-700 mb-4 flex items-center">
            <Calendar className="w-5 h-5 mr-2 text-primary-600" />
            Select Tax Year
          </label>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {TAX_YEARS.map((year) => (
              <button
                key={year}
                onClick={() => handleYearSelect(year)}
                className={`
                  p-4 rounded-lg border-2 transition-all duration-200 text-center
                  ${
                    selectedYear === year
                      ? 'border-primary-600 bg-primary-600 text-white shadow-md'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-primary-300 hover:shadow-sm'
                  }
                `}
              >
                <div className="font-semibold text-lg">{year}</div>
                {year === CURRENT_YEAR && (
                  <div className="text-xs mt-1 opacity-75">Current</div>
                )}
              </button>
            ))}
          </div>
          {errors.year && (
            <p className="mt-2 text-sm text-red-600">{errors.year}</p>
          )}
        </div>

        {/* Continue Button */}
        <div className="flex justify-end">
          <button
            onClick={handleContinue}
            disabled={!selectedCountry}
            className={`
              flex items-center space-x-2 px-8 py-3 rounded-lg font-semibold
              transition-all duration-200
              ${
                selectedCountry
                  ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-md hover:shadow-lg'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }
            `}
          >
            <span>Continue to Questionnaire</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>

        {/* Info Box */}
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> The questionnaire will be customized based on your country and tax year selection. 
            You can change these settings later if needed.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CountryYearSelection;






